/*
 * shareAction — the Share chip on the floorplan / package / display-home
 * detail intros.
 *
 * Native share sheet where the browser has one (every mobile browser, plus
 * Safari / Chrome / Edge on desktop), clipboard copy everywhere else. Both
 * paths confirm through the global toast store, which is already aria-live —
 * so there is no label swap on the chip and the actions row never reflows.
 *
 * The shared URL is the page's canonical (server-rendered by SEO Pro) rather
 * than location.href, so filter and utm_* query strings never ride along into
 * someone else's inbox. The share title comes from data-share-title on the
 * button, falling back to the document title.
 *
 * Alpine CSP build — the only template binding is the bare `share` method.
 */
export default function shareAction() {
    return {
        share() {
            const url = this.shareUrl();
            const payload = { title: this.$el.dataset.shareTitle || document.title, url };

            // canShare is the honest capability check: Firefox desktop has no
            // navigator.share at all, and some embedded webviews expose one
            // that rejects everything.
            if (navigator.share && navigator.canShare?.(payload) !== false) {
                navigator.share(payload).catch((error) => {
                    // Dismissing the sheet is a normal outcome, not a failure
                    // to fall back from.
                    if (error?.name === 'AbortError') return;
                    this.copy(url);
                });

                return;
            }

            this.copy(url);
        },

        /** Canonical if the page has one, current URL otherwise. */
        shareUrl() {
            return document.querySelector('link[rel="canonical"]')?.href || window.location.href;
        },

        copy(url) {
            this.writeToClipboard(url).then(
                () => this.$store.toasts.success('Link copied to clipboard'),
                () => this.$store.toasts.error('Couldn’t copy the link — please copy it from the address bar.'),
            );
        },

        /**
         * @param {string} url
         * @returns {Promise<void>}
         */
        writeToClipboard(url) {
            if (navigator.clipboard?.writeText) {
                return navigator.clipboard.writeText(url);
            }

            // The async clipboard needs a secure context; plain-http previews
            // (and older webviews) fall back to the legacy selection copy.
            return new Promise((resolve, reject) => {
                const field = document.createElement('textarea');
                field.value = url;
                field.setAttribute('readonly', '');
                field.style.position = 'fixed';
                field.style.top = '0';
                field.style.opacity = '0';
                document.body.appendChild(field);
                field.select();

                const copied = document.execCommand?.('copy');
                field.remove();

                return copied ? resolve() : reject(new Error('Clipboard unavailable'));
            });
        },
    };
}
